import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Heart, ArrowRight, Globe, CheckCircle2, Clock, Lock, Timer, Zap, ChevronDown } from "lucide-react";
import { ReputationBadge } from "../components/ReputationBadge";
import { useAuth } from "../context/AuthContext";
import { nextTierOf } from "../components/UpgradeCTA";

function hoursLeft(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expiring…";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h >= 1 ? `${h}h left` : `${m}m left`;
}

function activeStatus(m) {
  if (m.both_verified) return { label: "VERIFIED", tone: "text-sage-dark bg-sage/20", icon: CheckCircle2 };
  if (!m.articles) return { label: "NEEDS ARTICLE", tone: "text-terracotta bg-terracotta-light/40", icon: Clock };
  if (m.me_verified) return { label: "AWAITING PARTNER", tone: "text-slate-pass bg-muted", icon: Clock };
  return { label: "POST & VERIFY", tone: "text-terracotta bg-terracotta-light/40", icon: Clock };
}

function PartnerInfo({ m }) {
  return (
    <div className="flex items-center gap-4 min-w-0">
      <div className="h-16 w-16 rounded-xl overflow-hidden bg-muted shrink-0">
        {m.partner.image && <img src={m.partner.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-muted-foreground">
          {m.partner.url
            ? (<><Globe size={13} /><span className="font-mono text-xs truncate">{m.partner.url}</span></>)
            : (<><Lock size={13} /><span className="font-mono text-[11px] truncate">URL hidden until verified</span></>)}
        </div>
        <div className="font-serif font-semibold text-xl tracking-tight truncate flex items-center gap-2">
          <span className="truncate">{m.partner.name}</span>
          {m.partner.reputation && <ReputationBadge rep={m.partner.reputation} />}
        </div>
        <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{m.partner.niche} · DA {m.partner.da}</div>
      </div>
    </div>
  );
}

export default function Matches() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const load = () =>
    api.get("/matches").then(({ data }) => setData(data)).catch(() => setData({ matches: [], active_slots: 0, max_slots: 3 }));

  useEffect(() => { load(); }, []);

  const matches = data?.matches;
  const slotsFull = data ? data.active_slots >= data.max_slots : false;

  const startSwap = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/matches/${id}/initiate`);
      toast.success("Swap started — you have 48h to post & verify your backlink.");
      await load();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(formatApiError(err.response?.data?.detail), {
          duration: 7000, action: { label: "Upgrade", onClick: () => nav("/pricing") },
        });
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setBusyId(null);
    }
  };

  // Pipeline = I haven't initiated yet (unlimited). Partner-initiated ones surface first (urgent).
  const pipeline = (matches || [])
    .filter((m) => !m.me_initiated && !m.both_verified)
    .sort((a, b) => Number(b.partner_initiated) - Number(a.partner_initiated));
  const active = (matches || []).filter((m) => m.me_initiated && !m.both_verified);
  const completed = (matches || []).filter((m) => m.both_verified);

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[11px] tracking-widest text-muted-foreground">CONNECTIONS</div>
          <h1 className="font-serif font-bold text-5xl tracking-tight mt-2">Your matches</h1>
        </div>
        {data && (
          <div className={`rounded-full border px-4 py-2 text-sm shadow-soft ${slotsFull ? "border-terracotta/50 bg-terracotta-light/30" : "border-stone-200 bg-white"}`} data-testid="active-slots-counter">
            <span className="font-mono font-bold text-ink">{data.active_slots}</span>
            <span className="text-muted-foreground"> / {data.max_slots} active swaps</span>
          </div>
        )}
      </div>

      {data && (
        <div className="mt-4 flex items-center gap-2 text-[13px] text-muted-foreground">
          <Timer size={14} className="text-terracotta" />
          Match with as many sites as you like — you can run up to {data.max_slots} active swaps at once. Starting a swap begins a 48h post-and-verify window.
        </div>
      )}

      {matches === undefined && <p className="mt-8 font-mono text-sm text-muted-foreground animate-pulse">loading…</p>}

      {matches && matches.length === 0 && (
        <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-12 text-center shadow-soft">
          <div className="h-14 w-14 rounded-full bg-terracotta-light/40 flex items-center justify-center mx-auto"><Heart size={26} className="text-terracotta" /></div>
          <h3 className="font-serif font-semibold text-2xl mt-5">No matches yet</h3>
          <p className="text-muted-foreground text-sm mt-2">Head to the pool and start liking DA-matched sites.</p>
          <button onClick={() => nav("/feed")} data-testid="matches-to-pool"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-6 py-2.5 hover:bg-terracotta-dark active:scale-95 transition-transform font-medium">
            Open the pool <ArrowRight size={16} />
          </button>
        </div>
      )}

      {/* PIPELINE */}
      {pipeline.length > 0 && (
        <section className="mt-10" data-testid="pipeline-section">
          <div className="flex items-center justify-between">
            <h2 className="font-mono text-[11px] tracking-widest text-muted-foreground">PIPELINE · {pipeline.length}</h2>
            {slotsFull && (
              <span className="font-mono text-[10px] text-terracotta-dark flex items-center gap-2">
                Active slots full — finish or let one expire
                {nextTierOf(user?.plan) && (
                  <button onClick={() => nav("/pricing")} data-testid="matches-upgrade-slots"
                    className="underline text-primary hover:text-blue-400">Upgrade for more slots</button>
                )}
              </span>
            )}
          </div>
          <div className="mt-4 space-y-4">
            {pipeline.map((m) => {
              const partnerStarted = m.partner_initiated && !m.me_initiated;
              const left = hoursLeft(m.expires_at);
              const disabled = slotsFull || busyId === m.match_id;
              return (
                <div key={m.match_id} data-testid={`pipeline-row-${m.match_id}`}
                  className={`rounded-2xl border bg-white p-4 pr-5 transition-shadow ${partnerStarted ? "border-terracotta/50 shadow-card" : "border-stone-200 hover:shadow-card"}`}>
                  {partnerStarted && (
                    <div className="mb-3 flex items-center gap-2 rounded-xl bg-terracotta-light/40 px-3 py-2 text-[12px] text-terracotta-dark" data-testid={`partner-initiated-banner-${m.match_id}`}>
                      <Zap size={14} className="text-terracotta shrink-0" />
                      <span className="font-medium">Your match started this swap.</span>
                      <span>Accept & post fast{left ? ` — ${left}` : ""}, or it expires.</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4">
                    <button onClick={() => nav(`/matches/${m.match_id}`)} className="text-left min-w-0 flex-1">
                      <PartnerInfo m={m} />
                    </button>
                    <button onClick={() => startSwap(m.match_id)} disabled={disabled} data-testid={`start-swap-${m.match_id}`}
                      className={`shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-medium transition-transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${partnerStarted ? "bg-terracotta text-white hover:bg-terracotta-dark" : "bg-ink text-white hover:bg-ink/90"}`}>
                      {busyId === m.match_id ? "Starting…" : partnerStarted ? "Accept & start" : "Start swap"}
                      {busyId !== m.match_id && <ArrowRight size={15} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ACTIVE */}
      {active.length > 0 && (
        <section className="mt-10" data-testid="active-section">
          <h2 className="font-mono text-[11px] tracking-widest text-muted-foreground">ACTIVE SWAPS · {active.length}</h2>
          <div className="mt-4 space-y-4">
            {active.map((m) => {
              const s = activeStatus(m);
              const Icon = s.icon;
              const left = hoursLeft(m.expires_at);
              return (
                <button key={m.match_id} onClick={() => nav(`/matches/${m.match_id}`)} data-testid={`match-row-${m.match_id}`}
                  className="w-full flex items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white p-4 pr-6 hover:shadow-card transition-shadow text-left">
                  <PartnerInfo m={m} />
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={`flex items-center gap-1.5 font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full ${s.tone}`}>
                      <Icon size={13} /> {s.label}
                    </span>
                    {left && (
                      <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1"><Timer size={11} /> {left}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* COMPLETED */}
      {completed.length > 0 && (
        <section className="mt-10" data-testid="completed-section">
          <button onClick={() => setShowCompleted((v) => !v)} data-testid="toggle-completed"
            className="flex items-center gap-2 font-mono text-[11px] tracking-widest text-muted-foreground hover:text-ink transition-colors">
            <ChevronDown size={14} className={`transition-transform ${showCompleted ? "rotate-180" : ""}`} />
            COMPLETED · {completed.length}
          </button>
          {showCompleted && (
            <div className="mt-4 space-y-4">
              {completed.map((m) => (
                <button key={m.match_id} onClick={() => nav(`/matches/${m.match_id}`)} data-testid={`match-row-${m.match_id}`}
                  className="w-full flex items-center justify-between gap-4 rounded-2xl border border-sage/40 bg-sage/5 p-4 pr-6 hover:shadow-card transition-shadow text-left">
                  <PartnerInfo m={m} />
                  <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full text-sage-dark bg-sage/20 shrink-0">
                    <CheckCircle2 size={13} /> VERIFIED
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
